/**
 * データ取得・更新のフック群 (TanStack Query)。
 *
 * 更新系のエンドポイントはすべて「更新後のグループ全体」を返すため、
 * 応答をそのままキャッシュへ書き戻すだけで画面が最新化される。
 * 楽観的更新のような複雑な仕組みを持たず、読みやすさを優先している。
 */
import { DEFAULT_APP_FEATURES } from "@spli/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSpli, useSpliClient } from "./context.js";
export const queryKeys = {
    group: (shareToken) => ["group", shareToken],
    settlement: (shareToken, ignoreConstraints) => ["settlement", shareToken, ignoreConstraints === true],
    suggestions: (shareToken, query) => ["suggestions", shareToken, query],
    myGroups: () => ["me", "groups"],
    payoutAccount: () => ["me", "payout-account"],
    config: () => ["config"],
};
/**
 * サーバーが有効にしている機能。
 *
 * 取得できるまで、および取得に失敗した場合は「すべて無効」を返す。
 * 無効なものを一瞬表示してから消えるより、出てこないほうが混乱が少ない。
 */
export function useFeatures() {
    const client = useSpliClient();
    const query = useQuery({
        queryKey: queryKeys.config(),
        queryFn: () => client.getConfig(),
        // デプロイをまたがない限り変わらない値なので、長めにキャッシュする
        staleTime: 5 * 60_000,
        retry: 1,
    });
    return query.data?.features ?? DEFAULT_APP_FEATURES;
}
/**
 * グループの取得。
 *
 * 保存済みトークンの復元 (initializing) が終わるまで待ってから取得する。
 * サーバーはログイン中の取得を「閲覧した」とみなして履歴に追加するため、
 * 先に匿名で取りにいってしまうと、共有 URL を直接開いた初回が履歴に残らない。
 */
export function useGroup(shareToken) {
    const client = useSpliClient();
    const { initializing } = useSpli();
    return useQuery({
        queryKey: queryKeys.group(shareToken ?? ""),
        queryFn: () => client.getGroup(shareToken),
        enabled: Boolean(shareToken) && !initializing,
        staleTime: 10_000,
    });
}
/** 精算表の取得 */
export function useSettlement(shareToken, ignoreConstraints) {
    const client = useSpliClient();
    return useQuery({
        queryKey: queryKeys.settlement(shareToken ?? "", ignoreConstraints),
        queryFn: () => client.getSettlement(shareToken, ignoreConstraints),
        enabled: Boolean(shareToken),
    });
}
/**
 * 入力候補の取得。
 * 入力のたびに叩かれるので、同じクエリの結果は一定時間キャッシュする。
 */
export function useSuggestions(shareToken, query, options = {}) {
    const client = useSpliClient();
    return useQuery({
        queryKey: queryKeys.suggestions(shareToken ?? "", query),
        queryFn: () => client.getSuggestions(shareToken, { q: query }),
        enabled: Boolean(shareToken) && options.enabled !== false,
        staleTime: 30_000,
        placeholderData: (previous) => previous,
    });
}
/** ログインユーザーのグループ履歴 */
export function useMyGroups() {
    const client = useSpliClient();
    const { user } = useSpli();
    return useQuery({
        queryKey: queryKeys.myGroups(),
        queryFn: () => client.listMyGroups(),
        enabled: Boolean(user),
    });
}
/**
 * グループを更新する系のミューテーションを組み立てる共通処理。
 * 応答のグループをキャッシュへ反映し、精算・候補を無効化する。
 */
function useGroupMutation(shareToken, mutationFn) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn,
        onSuccess: (group) => {
            queryClient.setQueryData(queryKeys.group(group.shareToken), group);
            void queryClient.invalidateQueries({ queryKey: ["settlement", group.shareToken] });
            void queryClient.invalidateQueries({ queryKey: ["suggestions", group.shareToken] });
            if (shareToken && shareToken !== group.shareToken) {
                void queryClient.invalidateQueries({ queryKey: queryKeys.group(shareToken) });
            }
        },
    });
}
export function useCreateGroup() {
    const client = useSpliClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.createGroup(input),
        onSuccess: (group) => {
            queryClient.setQueryData(queryKeys.group(group.shareToken), group);
            void queryClient.invalidateQueries({ queryKey: queryKeys.myGroups() });
        },
    });
}
export function useUpdateGroup(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (input) => client.updateGroup(shareToken, input));
}
export function useAddMember(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (input) => client.addMember(shareToken, input));
}
export function useUpdateMember(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (variables) => client.updateMember(shareToken, variables.memberId, variables.input));
}
export function useRemoveMember(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (memberId) => client.removeMember(shareToken, memberId));
}
/** グループ内の「自分」を選んでアカウントと紐づける */
export function useClaimMember(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (memberId) => client.claimMember(shareToken, memberId));
}
export function useReleaseMember(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (memberId) => client.releaseMember(shareToken, memberId));
}
export function useUpsertConstraint(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (input) => client.upsertConstraint(shareToken, input));
}
export function useDeleteConstraint(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (constraintId) => client.deleteConstraint(shareToken, constraintId));
}
export function useCreateExpense(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (input) => client.createExpense(shareToken, input));
}
export function useUpdateExpense(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (variables) => client.updateExpense(shareToken, variables.expenseId, variables.input));
}
export function useDeleteExpense(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (expenseId) => client.deleteExpense(shareToken, expenseId));
}
export function useRecordSettlement(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (input) => client.recordSettlement(shareToken, input));
}
export function useDeleteSettlement(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (paymentId) => client.deleteSettlement(shareToken, paymentId));
}
/** 受取人本人による「受け取りました」の確認 */
export function useConfirmSettlement(shareToken) {
    const client = useSpliClient();
    return useGroupMutation(shareToken, (paymentId) => client.confirmSettlement(shareToken, paymentId));
}
// --- 送金の受取先 ------------------------------------------------------------
export function usePayoutAccount(options = {}) {
    const client = useSpliClient();
    const { user } = useSpli();
    return useQuery({
        queryKey: queryKeys.payoutAccount(),
        queryFn: () => client.getPayoutAccount(),
        // 機能が無効なときに叩くと 404 になるので、呼び出し側から止められるようにする
        enabled: Boolean(user) && (options.enabled ?? true),
    });
}
export function useSavePayoutAccount() {
    const client = useSpliClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.savePayoutAccount(input),
        onSuccess: (account) => {
            queryClient.setQueryData(queryKeys.payoutAccount(), account);
            // グループに表示される受取先も更新されるため、取得し直す
            void queryClient.invalidateQueries({ queryKey: ["group"] });
        },
    });
}
export function useDeletePayoutAccount() {
    const client = useSpliClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => client.deletePayoutAccount(),
        onSuccess: () => {
            queryClient.setQueryData(queryKeys.payoutAccount(), null);
            void queryClient.invalidateQueries({ queryKey: ["group"] });
        },
    });
}
// --- 認証 --------------------------------------------------------------------
/**
 * ① 確認コードの発行を依頼する。
 * 「そのメールアドレスが登録済みか」は結果から分からないようにしてある。
 */
export function useRequestLoginCode() {
    const client = useSpliClient();
    return useMutation({
        mutationFn: (input) => client.requestLoginCode(input),
    });
}
/**
 * ② 確認コードで認証する。未登録のメールアドレスならここで登録される。
 */
export function useVerifyLoginCode() {
    const client = useSpliClient();
    const { setSession } = useSpli();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.verifyLoginCode(input),
        onSuccess: async (response) => {
            await setSession(response.token, response.user);
            void queryClient.invalidateQueries({ queryKey: queryKeys.myGroups() });
            /*
             * 開いたことのあるグループを取り直す。
             * グループの取得はログイン中なら自動で履歴に追加するので、
             * 「先に開いて、あとからログインした」場合もこれで履歴に入る。
             * ログイン画面へ移動した時点で画面からは外れている (inactive) ため、
             * refetchType: "all" を指定しないと再取得されない。
             */
            void queryClient.invalidateQueries({ queryKey: ["group"], refetchType: "all" });
        },
    });
}
export function useLogout() {
    const { clearSession } = useSpli();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await clearSession();
        },
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: queryKeys.myGroups() });
        },
    });
}
/** 開いているグループを自分の履歴に保存する */
export function useLinkGroup(shareToken) {
    const client = useSpliClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => client.linkGroup(shareToken),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.myGroups() });
        },
    });
}
//# sourceMappingURL=queries.js.map